import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CrmLayout } from "@/components/crm/CrmLayout";
import { api } from "@/lib/api";
import { PIPELINE, PIPELINE_MAP, needLabel } from "@/lib/crmConstants";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, CheckCircle2, XCircle, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Prospects() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [needType, setNeedType] = useState("all");

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    if (needType !== "all") params.need_type = needType;
    api.get("/crm/prospects", { params }).then((r) => setItems(r.data));
  }, [q, status, needType]);

  useEffect(() => { load(); }, [load]);

  const exportData = async () => {
    try {
      const { data } = await api.get("/crm/export");
      const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "prospects.json"; a.click();
      toast.success(`${data.count} prospects exportés.`);
    } catch (e) { toast.error("Export réservé aux responsables."); }
  };

  return (
    <CrmLayout>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-display text-2xl font-bold text-slate-900">Prospects</h1><p className="text-slate-500 text-sm mt-1">{items.length} résultat(s)</p></div>
        {["administrator", "sales_manager"].includes(user.role) && (
          <Button variant="outline" onClick={exportData} data-testid="export-button"><Download size={16} className="mr-2" />Exporter</Button>
        )}
      </div>

      <Card className="p-4 mt-5">
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-3 text-slate-400" />
            <Input className="pl-9" placeholder="Rechercher (e-mail, nom, tél.)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="prospect-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous les statuts</SelectItem>{PIPELINE.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={needType} onValueChange={setNeedType}>
            <SelectTrigger data-testid="filter-needtype"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous les besoins</SelectItem><SelectItem value="mutuelle_france">Mutuelle France</SelectItem><SelectItem value="expatrie">Expatrié</SelectItem></SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="mt-5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-3 font-medium">Prospect</th><th className="px-4 py-3 font-medium">Besoin</th>
              <th className="px-4 py-3 font-medium">E-mail vérifié</th><th className="px-4 py-3 font-medium">Statut</th>
              <th className="px-4 py-3 font-medium">Créé le</th>
            </tr></thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} onClick={() => nav(`/espace-courtier/prospects/${p.id}`)} className="border-t border-slate-100 hover:bg-blue-50/40 cursor-pointer" data-testid={`prospect-row-${p.id}`}>
                  <td className="px-4 py-3"><div className="font-medium text-slate-900">{[p.first_name, p.last_name].filter(Boolean).join(" ") || "—"}</div><div className="text-slate-500 text-xs">{p.email}</div></td>
                  <td className="px-4 py-3"><Badge variant="secondary">{needLabel(p.need_type)}</Badge></td>
                  <td className="px-4 py-3">{p.email_verified ? <CheckCircle2 size={18} className="text-emerald-600" /> : <XCircle size={18} className="text-slate-300" />}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-xs"><span className={`w-2 h-2 rounded-full ${PIPELINE_MAP[p.status]?.color || "bg-slate-400"}`} />{PIPELINE_MAP[p.status]?.label || p.status}</span></td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{new Date(p.created_at).toLocaleDateString("fr-FR")}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Aucun prospect.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </CrmLayout>
  );
}
